<template>
  <section>
    <div class="d-flex align-items-center" v-if="!isOnWidgetMode">
      <h3>{{ title || alias }}</h3>
    </div>

    <Searcher
      v-if="!isOnWidgetMode && table.searcher"
      :hideFilters="!(!!table.filters)"
      :options="table.searcher"
      @onSearch="onSearch"
      @toggleFilters="filtersCollpse = !filtersCollpse"
    />

    <b-collapse v-model="filtersCollpse">
      <Filters
        v-if="!isOnWidgetMode && table.filters"
        :filters="table.filters"
        @change="onFilter"
        class="mb-4"
      />
    </b-collapse>

    <b-table
      v-if="mode === 'list'"
      v-bind="table"
      thead-class="text-nowrap"
      class="scroll"
      :key="key"
      @row-selected="onRowSelected"
    >

      <!-- Columnas -->
      <template #table-colgroup="scope">
        <col
          v-for="field in scope.fields"
          :key="field.key"
          :style="field.columnStyles"
          :class="{'primary-column': field.primaryColumn}"
        >
      </template>

      <!-- Celdas -->
      <template #cell()="data">
        <Component
          :is="cellComponent(data)"
          :value="data.value"
          :showDetails="data.item._showDetails"
          @details="data.toggleDetails(); toggleDetails($event)"
          v-bind="cellProps(data)"
        />
      </template>
      <template #cell(selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true"><i class="mdi mdi-check"></i></span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>

      <!-- Header -->
      <template #head(selected)="data">
        <b-form-checkbox
          @input="onSelectAllRows(data, $event)"
        ></b-form-checkbox>
      </template>

      <!-- Estados -->
      <template #table-busy>
        <div class="text-center text-secondary my-2">
          <b-spinner class="align-middle mr-4"></b-spinner>
          <strong>Cargando...</strong>
        </div>
      </template>
      <template #empty>
        <LmSvgIcon
          wrapperClass="d-flex h-100 justify-content-center align-items-center flex-column p-4"
          width="6em"
          icon="LmNoDataIcon"
          text="No se encontraron resultados, prueba con otra busqueda."
        />
      </template>

      <!-- Details -->
      <template #row-details>
      <div>
       <component :is="detailComponent" :value="detailValue"></component>
      </div>
      </template>
    </b-table>

    <template v-else-if="mode === 'box'">
      <b-row v-if="table.items.length">
        <b-col
          v-for="(item, index) in table.items"
          :key="index"
          :cols="primaryColumn.boxCols"
        >
          <b-link class="text-dark" :to="item[primaryColumn.key].click">
            <b-card class="item-box" no-body :style="`border-top: 5px solid ${accentColor}; min-height: 135px; max-height: 175px`">
              <Component
                :is="primaryColumn.component.value"
                :value="item[primaryColumn.key]"
                v-bind="primaryColumn.component.props"
                isBoxMode
              />
            </b-card>
          </b-link>
        </b-col>
      </b-row>
      <div v-else>
        <LmSvgIcon
          wrapperClass="d-flex h-100 justify-content-center align-items-center flex-column p-4"
          width="6em"
          icon="LmNoDataIcon"
          text="No se encontraron resultados, prueba con otra busqueda."
        />
      </div>
    </template>

    <div class="d-flex align-items-center mt-5" v-if="!isOnWidgetMode && itemsCount > 1">
      <span class="mr-4 mb-3">
        Mostrando {{ pagination.from }} a {{ pagination.to }} de {{ itemsCount }}
      </span>
      <b-pagination
        v-model="currentPage"
        :total-rows="itemsCount"
        :per-page="size"
        @input="loadTableItems"
      ></b-pagination>
    </div>

    <Buttons
      v-if="!isOnWidgetMode && !hideButtons && table.buttons"
      :buttons="table.buttons"
    />

  </section>
</template>

<script>
// Components
// Cells
import Badge from './components/cells/Badge'
import Keyword from './components/cells/Keyword'
import Actions from './components/cells/Actions'
import Avatar from './components/cells/Avatar'
import Profile from './components/cells/Profile'
import Date from './components/cells/Date'
// Documents
import DocumentNameStatus from './components/cells/documents/NameStatus'
import DocumentSigners from './components/cells/documents/Signers'
import DocumentProgress from './components/cells/documents/Progress'
import DocumentPrimary from './components/cells/documents/Primary'

// Processes
import ProcessPrimary from './components/cells/processes/Primary'

// Process Types
import ProcessTypePrimary from './components/cells/processtypes/Primary'

// Document Types
import DocumentTypePrimary from './components/cells/documenttypes/Primary'

// Users
import UserPrimary from './components/cells/users/Primary'

import Searcher from './components/Searcher'
import Filters from './components/Filters'
import Buttons from './components/Buttons'

// Utils
import commonsApi from '@/api/commons'

// Vuex
import { mapGetters } from 'vuex'

export default {
  props: {
    slug: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: null
    },
    isOnWidgetMode: {
      type: Boolean,
      default: false
    },
    hideButtons: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Searcher,
    Filters,
    Buttons,
    // Cells
    Badge,
    Keyword,
    Actions,
    Avatar,
    Profile,
    Date,
    // Documents
    DocumentPrimary,
    DocumentNameStatus,
    DocumentSigners,
    DocumentProgress,
    // Processes
    ProcessPrimary,
    // Process Types
    ProcessTypePrimary,
    // Document Types
    DocumentTypePrimary,
    // Users
    UserPrimary,
    'LoginHistory': () => import('./components/cells/users/components/LoginHistory'),
    'Audit': () => import('./components/cells/users/components/Audit'),
    'UserDetails': () => import('./components/cells/users/UserDetails')
  },
  provide () {
    const context = {}
    for (const key in this.context) {
      Object.defineProperty(context, key, {
        enumerable: true,
        get: () => this.context[key]
      })
    }
    return { context }
  },
  data () {
    return {
      key: 0,
      currentPage: 1,
      name: '',
      alias: '',
      size: 10,
      mode: 'list',
      itemsCount: 0,
      detailComponent: null,
      detailValue: null,
      table: {
        striped: true,
        borderless: false,
        bordered: false,
        hover: true,
        'show-empty': true,
        selectable: false,
        responsive: true,
        busy: false,
        fields: [],
        items: [],
        filters: null,
        searcher: null,
        buttons: null
      },
      search: {},
      filtersToApply: [],
      filtersCollpse: true,
      serviceSource: null
    }
  },
  watch: {
    slug: {
      async handler (val) {
        try {
          await this.loadTable()
          this.refreshTable()
        } catch (error) {
          console.log(error)
        }
      }
    }
  },
  computed: {
    ...mapGetters('settings', ['accentColor']),
    context () {
      return {
        serviceSource: this.serviceSource
      }
    },
    pagination () {
      return {
        from: ((this.size * this.currentPage) - this.size) + 1,
        to: this.itemsCount < this.size ? this.itemsCount : this.size * this.currentPage
      }
    },
    primaryColumn () {
      return this.table.fields.find(el => el?.primaryColumn)
    }
  },
  methods: {
    toggleDetails ({ component, value }) {
      this.detailComponent = component
      this.detailValue = value
    },
    onSelectAllRows (data, event) {
      if (event) {
        data.selectAllRows()
      } else {
        data.clearSelected()
      }
    },
    onRowSelected (event) {
      this.$emit('onSelect', event)
    },
    refreshTable () {
      // NOTA. necesito refrescar la tabla cada vez
      // que se actualizan los items por fallas de renderizado
      // TODO. Buscar otra mejor solucion
      this.key++
    },
    cellProps (event) {
      const props = event.field?.component?.props
      if (props) {
        return props
      }
      return {}
    },
    cellComponent (event) {
      return event.field?.component?.value || 'Keyword'
    },
    async onSearch (event) {
      this.search = event
      await this.loadTableItems()
    },
    async onFilter (event) {
      this.filtersToApply = event
      await this.loadTableItems()
    },
    async loadTable () {
      const res = await commonsApi.axiosGetWrapper(`/datatables/${this.slug}/table`)
      this.name = res.data.name
      this.alias = res.data.alias
      this.size = res.data.size
      this.mode = res.data.mode
      this.table.selectable = res.data.selectable
      this.table.fields = res.data.table.fields
      this.table.filters = res.data.table.filters || null
      this.table.buttons = res.data.table.buttons || null
      this.table.searcher = res.data.table.searcher || null
      this.serviceSource = res.data.serviceSource || null

      if (this.table.selectable) {
        this.table.fields.unshift({
          key: 'selected',
          label: 'Seleccionado'
        })
      }
    },
    async loadTableItems () {
      try {
        this.table.busy = true
        const res = await commonsApi.axiosPostWrapper('/datatables/items', {
          slug: this.slug,
          page: this.currentPage,
          search: this.search,
          filters: this.filtersToApply
        })
        this.table.items = res.data.items
        this.itemsCount = res.data.count
        this.refreshTable()
      } catch (error) {
        console.log(error)
      } finally {
        this.table.busy = false
      }
    }
  },
  async created () {
    try {
      await this.loadTable()
      await this.loadTableItems()
    } catch (error) {
      console.log(error)
    }
  }
}
</script>

<style lang="sass" scoped>
.item-box
  transition: all .4s ease-in-out
  &:hover
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15)
.primary-column
  border-right: 1px solid #17a2b842
  border-left: 1px solid #17a2b842
.edit-table-button
  color: #afafaf
  margin-left: 1rem
  font-size: 1.25rem
.scroll
    flex-wrap: nowrap
    overflow-x: auto
    padding-bottom: 1rem
    &:hover
      &::-webkit-scrollbar-thumb
        border-radius: 20px
        background: #888
    &::-webkit-scrollbar
      height: 8px
      width: 8px
    &::-webkit-scrollbar-thumb
      border-radius: 20px
      background: #888
    &::-webkit-scrollbar-thumb:hover
      background: #555
</style>
